/**
 * Attendance Login Component with CAPTCHA support
 *
 * Production note:
 * ETC portal blocks many datacenter/proxy IPs. In production we MUST use
 * a dedicated proxy backend with a stable outbound IP (VITE_PROXY_URL).
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  AlertCircle,
  Shield,
  Eye,
  EyeOff,
  LogIn,
  RefreshCw
} from 'lucide-react';
import { attendanceStorage } from '@/services/attendanceStorage';

interface AttendancePortalFrameProps {
  onDataReceived: (data: string) => void;
  onCancel: () => void;
}

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL as string | undefined)?.replace(/\/$/, '');

type ProxyCaptchaResponse =
  | { success: true; sessionId: string; captchaImage: string }
  | { success: false; error: string };

type ProxyLoginResponse =
  | {
      success: true;
      data: {
        studentName?: string;
        courses: Array<{
          courseName: string;
          presents: number;
          absents: number;
          officialLeave?: number;
          totalClasses: number;
          percentage: number;
        }>;
        fetchedAt: string;
      };
    }
  | { success: false; error: string };

export function AttendancePortalFrame({ onDataReceived, onCancel }: AttendancePortalFrameProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCaptcha, setLoadingCaptcha] = useState(true);
  const [error, setError] = useState('');
  const [hasStoredCreds, setHasStoredCreds] = useState(false);
  const [proxyError, setProxyError] = useState(false);

  useEffect(() => {
    loadStoredCredentials();
    loadCaptcha();
  }, []);

  const loadStoredCredentials = async () => {
    try {
      const creds = await attendanceStorage.getCredentials();
      if (creds) {
        setUsername(creds.username);
        setPassword(creds.password);
        setRememberMe(true);
        setHasStoredCreds(true);
      }
    } catch (err) {
      console.error('Error loading stored credentials:', err);
    }
  };

  const loadCaptcha = async () => {
    setLoadingCaptcha(true);
    setError('');
    setCaptchaText('');
    setProxyError(false);

    try {
      if (!PROXY_BASE) {
        throw new Error('Attendance proxy is not configured. Please set VITE_PROXY_URL.');
      }

      const resp = await fetch(`${PROXY_BASE}/captcha`, { method: 'GET' });
      const data = (await resp.json()) as ProxyCaptchaResponse;

      if (!data.success) {
        throw new Error(data.error || 'Failed to load captcha');
      }

      setSessionId(data.sessionId);
      setCaptchaImage(data.captchaImage);
    } catch (err) {
      console.error('Error loading captcha:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect to attendance proxy.');
      setProxyError(true);
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim() || !captchaText.trim()) {
      setError('Please fill in all fields including the captcha');
      return;
    }
    if (!sessionId) {
      setError('Session expired. Please refresh captcha.');
      loadCaptcha();
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (!PROXY_BASE) {
        throw new Error('Attendance proxy is not configured. Please set VITE_PROXY_URL.');
      }

      const resp = await fetch(`${PROXY_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          username,
          password,
          captchaText,
        }),
      });

      const payload = (await resp.json()) as ProxyLoginResponse;
      if (!payload.success) {
        throw new Error(payload.error || 'Login failed');
      }

      // Save credentials if remember me
      if (rememberMe) {
        await attendanceStorage.saveCredentials(username, password);
      } else {
        await attendanceStorage.clearCredentials();
      }

      // Return data
      onDataReceived(JSON.stringify(payload.data));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      if (err instanceof Error && err.message.includes('captcha')) {
        loadCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearCredentials = async () => {
    await attendanceStorage.clearCredentials();
    setUsername('');
    setPassword('');
    setRememberMe(false);
    setHasStoredCreds(false);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle>ETC Portal Login</CardTitle>
        <CardDescription>
          Enter your credentials to fetch attendance
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Your credentials are processed in your browser only. Nothing is sent to our servers.
          </AlertDescription>
        </Alert>

        {proxyError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Unable to connect to attendance proxy. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username / Roll Number</Label>
            <Input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <Label htmlFor="remember" className="text-sm cursor-pointer">
                Remember me
              </Label>
            </div>
            {hasStoredCreds && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearCredentials}
                className="text-xs text-muted-foreground"
              >
                Clear saved login
              </Button>
            )}
          </div>

          <div className="space-y-2">
            <Label>Security Code</Label>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              {loadingCaptcha ? (
                <div className="flex items-center justify-center w-full h-16">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : captchaImage ? (
                <>
                  <img
                    src={captchaImage}
                    alt="Captcha"
                    className="h-12 border rounded"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={loadCaptcha}
                    disabled={loading}
                    title="Refresh captcha"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <div className="flex flex-col items-center w-full gap-2">
                  <p className="text-sm text-muted-foreground">Failed to load captcha</p>
                  <Button type="button" variant="outline" size="sm" onClick={loadCaptcha}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Retry
                  </Button>
                </div>
              )}
            </div>
            <Input
              type="text"
              placeholder="Enter the code shown above"
              value={captchaText}
              onChange={(e) => setCaptchaText(e.target.value)}
              disabled={loading || loadingCaptcha || !captchaImage}
              autoComplete="off"
            />
          </div>

          {error && !proxyError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              className="flex-1"
              disabled={loading || loadingCaptcha || !username || !password || !captchaText || !captchaImage}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Fetch Attendance
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
